import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useClassData, useClasses } from 'entities/classes';
import { Dropdown } from 'shared/ui/dropdown';
import {
  eventService,
  XApiEventKeys,
  getPage,
  generateCommonContextEventPayload,
  generateClassFilterResultEventPayload,
} from 'shared/api/event-service';

import {
  getClassName,
  getLastSelectedTeacherClassByName,
  resetFilters,
  saveLastSelectedTeacherClassAs,
  setSelectedClass,
} from '../model';
import { ALL_CLASSES, LAST_SELECTED_TEACHER_CLASS_ID } from '../constants';

import styles from './styles.module.scss';

export const ClassFilter = () => {
  const { t } = useTranslation();

  const [initialSelectedId, setInitialSelectedId] = useState('');
  const { isFetching, classes } = useClasses();
  const { isFetching: isFetchingClassData, classData } = useClassData();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!classes || !classData) return;

    if (classes.length === 1) {
      dispatch(setSelectedClass(classes));
      setInitialSelectedId(classes[0].classKey);
      return;
    }

    const lastSelectedClassId = getLastSelectedTeacherClassByName(
      LAST_SELECTED_TEACHER_CLASS_ID,
    );
    const actualLastSelectedClassId =
      classes.find((cl) => cl.classKey === lastSelectedClassId)?.classKey ??
      ALL_CLASSES;

    dispatch(
      setSelectedClass(
        actualLastSelectedClassId === ALL_CLASSES
          ? classes
          : [classData[actualLastSelectedClassId]],
      ),
    );
    setInitialSelectedId(actualLastSelectedClassId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes, classData, dispatch]);

  const handleSelect = (id: string, _: string, eventType?: string) => {
    if (!classes || !classData) return;

    const areMultipleClassesSelected = id === ALL_CLASSES;
    const selectedClass = areMultipleClassesSelected
      ? classes
      : [classData[id]];

    dispatch(setSelectedClass(selectedClass));
    saveLastSelectedTeacherClassAs(LAST_SELECTED_TEACHER_CLASS_ID, id);
    dispatch(resetFilters());

    const classFiltersApplied = selectedClass.map((cl) => cl.classKey);

    eventService.send(
      XApiEventKeys.classFiltered,
      generateClassFilterResultEventPayload({
        classFiltersApplied,
        classFilterIsAll: areMultipleClassesSelected,
      }),
      generateCommonContextEventPayload({
        url: window.location.href,
        page: getPage(),
        isRightClick: eventType === 'contextmenu',
      }),
    );
  };

  return (
    <Dropdown
      classNames={{
        button: clsx('rhythm-button', styles.dropdownBtn),
        container: styles.dropdownContainer,
        menu: styles.dropdownMenu,
      }}
      isLoading={isFetching || isFetchingClassData}
      dropdownText="Select Class"
      onSelect={handleSelect}
      initialSelectedId={initialSelectedId}
      testId="class-filter"
    >
      {classes && classes.length > 1 && (
        <Dropdown.Item id={ALL_CLASSES}>{t('All Classes')}</Dropdown.Item>
      )}
      {classes?.map((cl) => (
        <Dropdown.Item key={cl.classKey} id={cl.classKey}>
          {getClassName(cl)}
        </Dropdown.Item>
      ))}
    </Dropdown>
  );
};
